import React, { useEffect } from "react";

import { BrowserRouter, Routes, Route } from "react-router-dom";
import Header from "./components/Header/Header";
import HomeScreen from "./screens/customer/HomeScreen/HomeScreen";
import ParlorDetailsScreen from "./screens/customer/ParlorDetailsScreen/ParlorDetailsScreen";
import SelectSlotScreen from "./screens/customer/SelectSlotScreen/SelectSlotScreen";
import BookingSummaryScreen from "./screens/customer/BookingSummaryScreen/BookingSummaryScreen";
import Footer from "./components/Footer/Footer";
import Dashboard from "./screens/experts/Dashboard/Dashboard";
import SignIn from "./screens/login/signIn/SignIn";
import SignUp from "./screens/login/signUp/SignUp";
import CustomerSignUp from "./screens/login/signUp/CustomerSignUp";
import MyProfileScreen from "./screens/MyProfileScreen/MyProfileScreen";
import { Provider } from "react-redux";
import store from "./store/Store";
import OnboardScreen from "./screens/OnboardScreen/OnboardScreen";
import EditProfileScreen from "./screens/EditProfileScreen/EditProfileScreen";
import ServicesListingScreen from "./screens/ServicesListingScreen/ServicesListingScreen";
import NearByShops from "./screens/NearByShopsList/NearByShops";
import ShopsScreen from "./screens/ShopsScreen/ShopsScreen";
import SupportScreen from "./screens/SupportScreen/SupportScreen";
import MyBookingsScreen from "./screens/MyBookingsScreen/MyBookingsScreen";
import SettingsScreen from "./screens/SettingsScreen/SettingsScreen";
import WishlistScreen from "./screens/WishlistScreen/WishlistScreen";
import HistoryScreen from "./screens/HistoryScreen/HistoryScreen";
import AdminLogin from "./screens/admin/AdminLogin/AdminLogin";
import AdminDashboard from "./screens/admin/AdminDashboard/AdminDashboard";
import PrivacyScreen from "./screens/PolicyScreens/PrivacyScreen";
import TermsScreen from "./screens/PolicyScreens/TermsScreen";
import CookiesScreen from "./screens/PolicyScreens/CookiesScreen";
import SitemapScreen from "./screens/SitemapScreen/SitemapScreen";
import NotFoundScreen from "./screens/NotFoundScreen/NotFoundScreen";
import Seo from "./components/Seo/Seo";
import { PAGES } from "./seo/seo.config";
import {
  breadcrumbSchema,
  webPageSchema,
} from "./seo/structuredData";
import { clarity } from "react-microsoft-clarity";
import AboutUsScreen from "./screens/AboutUsScreen/AboutUsScreen";

/**
 * Attaches route metadata to a screen.
 *
 * Exactly one <Seo /> may be mounted at a time (each instance rewrites the
 * managed head tags), so screens that build their metadata from API data —
 * ParlorDetailsScreen, ShopsScreen, SitemapScreen, NotFoundScreen — render
 * their own <Seo /> and are wired up here without a wrapper.
 */
const withSeo = (page, element, extra = {}) => (
  <>
    <Seo
      title={page.title}
      description={page.description}
      keywords={page.keywords}
      canonical={page.canonical || page.path}
      noindex={page.noindex}
      {...extra}
    />
    {element}
  </>
);

/** Breadcrumb + WebPage schema for a simple static page. */
const staticPageSchema = (page, { type = "WebPage" } = {}) => [
  breadcrumbSchema([{ name: page.title, path: page.canonical || page.path }]),
  webPageSchema({
    title: page.title,
    description: page.description,
    path: page.canonical || page.path,
    type,
  }),
];

function App() {
  useEffect(() => {
    clarity.init(process.env.REACT_APP_CLARITY_ID);
  }, []);
  return (
    <>
      <Provider store={store}>
        <BrowserRouter>
          <Routes>
            {/* ── Public, indexable ── */}
            <Route path="/" element={<HomeScreen />} />
            <Route path="/shops" element={<ShopsScreen />} />
            <Route path="/nearby" element={<NearByShops />} />
            <Route path="/sitemap" element={<SitemapScreen />} />
            <Route
              path="/shop/:location/:slug"
              element={<ParlorDetailsScreen />}
            />

            {/* Brought across from main; given the same SEO treatment as the
                other public pages. */}
            <Route
              path="/about-us"
              element={withSeo(PAGES.aboutUs, <AboutUsScreen />, {
                jsonLd: staticPageSchema(PAGES.aboutUs, { type: "AboutPage" }),
              })}
            />

            <Route
              path="/contact"
              element={withSeo(PAGES.contact, <SupportScreen />, {
                jsonLd: staticPageSchema(PAGES.contact, { type: "ContactPage" }),
              })}
            />
            {/* Same screen as /contact — canonicalised there so the three
                URLs aren't treated as duplicate content. */}
            <Route
              path="/support"
              element={withSeo(PAGES.support, <SupportScreen />)}
            />
            <Route path="/help" element={withSeo(PAGES.help, <SupportScreen />)} />

            <Route
              path="/signup"
              element={withSeo(PAGES.signup, <SignUp />, {
                jsonLd: staticPageSchema(PAGES.signup),
              })}
            />

            <Route
              path="/privacy"
              element={withSeo(PAGES.privacy, <PrivacyScreen />, {
                type: "article",
                jsonLd: staticPageSchema(PAGES.privacy),
              })}
            />
            <Route
              path="/terms"
              element={withSeo(PAGES.terms, <TermsScreen />, {
                type: "article",
                jsonLd: staticPageSchema(PAGES.terms),
              })}
            />
            <Route
              path="/cookies"
              element={withSeo(PAGES.cookies, <CookiesScreen />, {
                type: "article",
                jsonLd: staticPageSchema(PAGES.cookies),
              })}
            />

            {/* ── Private / transactional: noindex ── */}
            <Route path="/signin" element={withSeo(PAGES.signin, <SignIn />)} />
            <Route
              path="/signup/type=customer"
              element={withSeo(PAGES.customerSignup, <CustomerSignUp />)}
            />
            <Route
              path="/parlor/:id/service/:serviceId"
              element={withSeo(PAGES.slotSelection, <SelectSlotScreen />)}
            />
            <Route
              path="/parlor/:id/services"
              element={withSeo(PAGES.serviceListing, <ServicesListingScreen />)}
            />
            <Route
              path="/parlor/:id/slot-selection"
              element={withSeo(PAGES.slotSelection, <SelectSlotScreen />)}
            />
            <Route
              path="/summary"
              element={withSeo(PAGES.summary, <BookingSummaryScreen />)}
            />
            <Route
              path="/my-bookings"
              element={withSeo(PAGES.myBookings, <MyBookingsScreen />)}
            />
            <Route
              path="/wishlist"
              element={withSeo(PAGES.wishlist, <WishlistScreen />)}
            />
            <Route
              path="/history"
              element={withSeo(PAGES.history, <HistoryScreen />)}
            />
            <Route
              path="/settings"
              element={withSeo(PAGES.settings, <SettingsScreen />)}
            />

            <Route
              path="/shop/dashboard"
              element={withSeo(PAGES.shopDashboard, <Dashboard />)}
            />
            <Route
              path="/shop/profile"
              element={withSeo(PAGES.shopProfile, <MyProfileScreen />)}
            />
            <Route
              path="/shop/edit-profile"
              element={withSeo(PAGES.shopEditProfile, <EditProfileScreen />)}
            />
            <Route
              path="/shop/onboard"
              element={withSeo(PAGES.shopOnboard, <OnboardScreen />)}
            />

            <Route
              path="/admin/login"
              element={withSeo(PAGES.adminLogin, <AdminLogin />)}
            />
            <Route
              path="/admin/dashboard"
              element={withSeo(PAGES.adminDashboard, <AdminDashboard />)}
            />

            {/* Catch-all: without this, unknown URLs rendered a blank page. */}
            <Route path="*" element={<NotFoundScreen />} />
          </Routes>
        </BrowserRouter>
      </Provider>
    </>
  );
}

export default App;
